import { useState } from 'react';

import { downloadUrlA } from '@iconik-io/core/src/download_url_a';
import { httpGetAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId } from '@iconik-io/iconik-api/src/services/files';
import {
  Button,
  Dialog,
  DialogActionButtons,
  DialogContent,
  DialogHeading,
  Icon,
  Modal,
  PanelHeader,
  PanelHeading,
  Tooltip,
  TooltipTrigger,
} from '@iconik-io/uad';

import { useAppDispatch } from '../../../../../redux/redux_hooks';
import type { CommentAttachmentTileItem } from '../comment_attachment_tiles/comment_attachment_tiles';

import styles from './comment_attachment_preview.module.css';

export type CommentAttachmentPreviewItem = CommentAttachmentTileItem & {
  assetId: string;
  segmentId: string;
};

type CommentAttachmentPreviewProps = {
  attachment: CommentAttachmentPreviewItem;
  onClose: () => void;
};

export function CommentAttachmentPreview({
  attachment,
  onClose,
}: CommentAttachmentPreviewProps) {
  const dispatch = useAppDispatch();
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const url = attachment.url ?? undefined;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // The preview url shows the file inline; a download needs one signed
      // with an attachment content disposition.
      const { body } = await dispatch(
        httpGetAssetsByAssetIdSegmentsBySegmentIdAttachmentsByAttachmentId(
          attachment.assetId,
          attachment.segmentId,
          attachment.id,
          { content_disposition: 'attachment' },
        ),
      );
      if (body.url) {
        downloadUrlA(body.url, attachment.name);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section
      className={styles.preview}
      aria-label={`Preview of ${attachment.name}`}
    >
      <PanelHeader className={styles.header}>
        <PanelHeading>{attachment.name}</PanelHeading>
        <div className={styles.header_actions}>
          <TooltipTrigger>
            <Button
              variant="tertiary"
              icon={<Icon name="load_down" />}
              aria-label={`Download ${attachment.name}`}
              isPending={isDownloading}
              onPress={handleDownload}
            />
            <Tooltip>Download</Tooltip>
          </TooltipTrigger>
          <TooltipTrigger>
            <Button
              variant="tertiary"
              icon={<Icon name="close" />}
              aria-label="Close preview"
              onPress={onClose}
            />
            <Tooltip>Close preview</Tooltip>
          </TooltipTrigger>
        </div>
      </PanelHeader>
      <div className={styles.body}>
        <Button
          className={styles.enlarge_button}
          variant="tertiary"
          aria-label={`Enlarge ${attachment.name}`}
          onPress={() => setIsEnlarged(true)}
        >
          <img className={styles.image} src={url} alt={attachment.name} />
        </Button>
      </div>
      <Modal isDismissible isOpen={isEnlarged} onOpenChange={setIsEnlarged}>
        <Dialog size="xl">
          <DialogHeading isDismissible>{attachment.name}</DialogHeading>
          <DialogContent>
            <img
              className={styles.enlarged_image}
              src={url}
              alt={attachment.name}
            />
          </DialogContent>
          <DialogActionButtons>
            <Button
              variant="primary"
              icon={<Icon name="load_down" />}
              isPending={isDownloading}
              onPress={handleDownload}
            >
              Download
            </Button>
          </DialogActionButtons>
        </Dialog>
      </Modal>
    </section>
  );
}
